import {observable, action, computed, set } from "mobx";
import AppConstant from "../appConstants";
import { cloneDeep } from "lodash/lang";
import { getFilteredData } from "../components/common/filterUtilities";
import CheckViolation from "../components/common/checkViolation";
import { routeHasHoldPro, stopHasHoldPro, shipmentOnHold } from "../components/modules/IBPlanning/RoutesView/utils";
import { RMAXLogger } from "@RMAXLogger";

class IBPlanningStore{
    //original data Sources
    @observable routesList=[];
    @observable stopsList=[];
    @observable shipmentsList=[];
    @observable newShipments = {};
    @observable quickFilter=AppConstant.EMPTY_STRING;
    @observable kpiData=[];
    @observable planDate= null;
    @observable disableStoreUpdate=false;
    @observable isLassoRouteCreated=false;
    @observable newRoutesTobeCleared=null;
    @observable isDataRefreshInProgress=false;
    @observable isPublishInProgress=false;
    @observable isPrintManifestInProgress=false;

    // derived data sources
    @observable routeWithStops=[];
    @observable filteredRouteStops=[];
    @observable selectedRouteNameList=[];
    @observable selectedShipmentIDList=[];

    @observable plannedShipmentsList=[];
    @observable filteredRouteShipments=[];
    @observable filteredShipments=[];
    @observable routeMapColorList=[];
    @observable filteredShipmentsOnMap=[];
    @observable staticClientData = null;
    @observable doorList = [];
    @observable driverPreferencesList = [];
    @observable holidayOptions = [];
    @observable routeProLateStatus = {};
    @observable shipmentGridMessage = "loading";
    @observable stopGridMessage = "loading";
    @observable routeGridMessage = "loading";
    @observable quickFilterList = [];
    @observable shipmentAutoSelectedFilter = [];
    @observable equipPref = {
        maxEquipSizes: [],
        minEquipSizes: [],
        fixedEquipSizes: []
    };
    @observable routeMapControls = {
        isMapLegendEnabled: false,
        isRouteAnimationEnabled: true,
        isToggleAnchorPointsEnabled: false
    };
   
    constructor(){
        const newShipments = sessionStorage.getItem("newShipments") ? JSON.parse(sessionStorage.getItem("newShipments")) : {};
        set(this.newShipments, newShipments);
    }

    @action updateDataRefreshProgress(value){
        this.isDataRefreshInProgress=value; 
    }

  // Updating orignal dataSource
    @action addRoutes(routesList, routeMapColorList){
        this.routesList.replace(this.assignColorToRoute(routesList, routeMapColorList));
    }

    @action addStops(stopsList){
        this.stopsList.replace(stopsList);
        this.setrouteProLateStatus();
    }

    @action setrouteProLateStatus(){
        if(!this.planDate){
            this.routeProLateStatus = {};
            return;
        }
       
        this.routeProLateStatus = this.stopsList.reduce((acc, stop) => {
            const violation =  CheckViolation.checkPrepTimeGreaterThanStartTime({data: stop,planDate: this.planDate});
            if(violation){
                acc[stop.routeId] = violation
            }
            return acc;
        }, {});
    }

    @action addShipments(shipmentList, enableHoldPros=false){
        const updatedShipmentList = shipmentList.map((shipment) => {
            return {
                ...shipment,
                ...shipmentOnHold({enableHoldPros, shipmentData: shipment})
            }
        }).sort((shipment1, shipment2) => (shipment1?.routeName ?? '') > (shipment2?.routeName ?? '') ? -1 : 1);
        this.shipmentsList.replace(updatedShipmentList);
        this.filterPlannedShipments();
        this.filterShipments();
        this.filterRouteShipments();
        this.updateFilteredShipments(updatedShipmentList);
        this.updateEquipmentPreferenceFilter(updatedShipmentList);
    }

  @action updateEquipmentPreferenceFilter(shipmentList) {
    let [maxEquipSizes, minEquipSizes, fixedEquipSizes] = [[], [], []];
    shipmentList.forEach((shipment) => {
      const { maxFeasibleTrailerLength, minEquipmentSize, fixedEquipmentSize } = shipment;
      if (maxFeasibleTrailerLength && !maxEquipSizes.includes(maxFeasibleTrailerLength)) {
        maxEquipSizes.push(maxFeasibleTrailerLength);
      }
      if (minEquipmentSize && !minEquipSizes.includes(minEquipmentSize)) {
        minEquipSizes.push(minEquipmentSize);
      }
      if (fixedEquipmentSize && !fixedEquipSizes.includes(fixedEquipmentSize)) {
        fixedEquipSizes.push(fixedEquipmentSize);
      }
    });
    this.equipPref = {
      maxEquipSizes,
      minEquipSizes,
      fixedEquipSizes,
    }
  }

  @action updateKPIData(kpiData){
        this.kpiData.replace(kpiData);
    }

    @action updateNewRoutesToBeCleared(routeName=null){
        this.newRoutesTobeCleared = routeName;
    }

    @action updateNewShipments(newShipments){
        // DEFENSIVE GUARD: Check and clean falsy values before storing
        if (newShipments && Array.isArray(newShipments.data)) {
            const hasFalsyValues = newShipments.data.some(x => !x);
            if (hasFalsyValues) {
                RMAXLogger.error('[GUARD] updateNewShipments: Attempting to store falsy values in data array!', {
                    scenarioID: newShipments.scenarioID,
                    fullData: JSON.stringify(newShipments.data)
                });
                // CLEAN: Filter out falsy values before storing
                newShipments.data = newShipments.data.filter(Boolean);
            }
        }
        
        if(!newShipments && !newShipments.data){
            sessionStorage.removeItem("newShipments");
        }
        if(newShipments && newShipments.data && !newShipments.data.length){
            this.updateNewRoutesToBeCleared();
        }
        sessionStorage.setItem("newShipments", JSON.stringify(newShipments));
        set(this.newShipments, newShipments);
    }

    @action refreshDataSources(updatedData){
        const {routesData, stopsData, newShipments, iconsColor, planDate, getNewBills, filteredRouteStops = null, enableHoldPros = false} = updatedData;
        const updatedRoutesData = routesData.map((route) => {
        return {
          ...route,
          isCustomerClosed: this.isCustomerClosedForRoutes({
            routeId: route.routeId,
            stopsList: stopsData,
            shipmentList: this.shipmentsList
        }),
        ...routeHasHoldPro({shipmentsList: this.shipmentsList, route, enableHoldPros})
        };
      })
    this.addRoutes(updatedRoutesData, iconsColor);
    const updatedStopsData = stopsData.map((stop) => {
        return {
          ...stop,         
        ...stopHasHoldPro({shipmentsList: this.shipmentsList, stop, enableHoldPros})
        };
      })
    this.addStops(updatedStopsData);
        this.updatePlanDate(planDate);
        if (filteredRouteStops) {
            this.filteredRouteStops.replace(filteredRouteStops);
        }
        if(getNewBills){
            const newBillsData = {
                scenarioID:newShipments.scenarioID,
                data:this.newShipments.data ? this.newShipments.data.concat(newShipments.data) : newShipments.data
            }
            this.updateNewShipments(newBillsData);
        }
    }

    @action updatePlanDate(planDate){
        this.planDate = planDate;
        this.setrouteProLateStatus();
    }

    @action setdisableStoreUpdate(value){
        this.disableStoreUpdate=value;
    }

    // updating derived data sources
    // ROUTES changes
    @action filterRouteStops(){
       const filteredRouteStopList =  this.selectedRouteNameList.length > 0 ?
                                      this.stopsList.filter(stop=>this.selectedRouteNameList.includes(stop.routeName)) : this.stopsList;
        this.filteredRouteStops.replace(cloneDeep(filteredRouteStopList));
    }

    @action setReseqeuncedRouteStops(resequencedStops){
       // This will used in RoutesMap and Stops grid, the data can be changed without affecting the main data source(Eg: Stop sequence update)
       // so that it can be easily discarded if changes are to be rejected
        this.filteredRouteStops.replace(cloneDeep(resequencedStops));
        this.updateRouteWithStopsAndShipments();
    }

    @action refreshFilterStops(){
        this.updateSelectedRoutes(this.selectedRouteNameList);
    }

    @action updateSelectedRoutes(routeNameList){
            this.selectedRouteNameList.replace(routeNameList);
            this.filterRouteShipments();
            this.filterRouteStops();
            this.updateRouteWithStopsAndShipments();
    }

    @computed get selectedRoutesAnchorPointList(){
        return this.routesList.reduce((acc, route) => {
            if (route.anchorPointName && this.selectedRouteNameList.includes(route.routeName)) {
                acc.push(route.anchorPointName);
            }
            return acc;
        }, []);
    }

    @action updateSelectedShipments(shipmentsList){
        this.selectedShipmentIDList.replace(shipmentsList);
    }

    updateRouteWithStopsAndShipments(){
        let selectedRoutes = this.routesList.filter(route => this.selectedRouteNameList.includes(route.routeName));
        const routeWithStops =  selectedRoutes.map(route=>{
            if(!route.routeStops){
                route.routeStops =[];
            }
            if(!route.routeShipments){
                route.routeShipments =[];
            }
            route.routeStops.replace(this.filteredRouteStops.filter(stop => stop.routeName === route.routeName ));
            if(route.isTrapRoute || !route.routeId){ // routeShipments are only used to be displayed Routes map when route is a trap route
                route.routeShipments.replace(this.filteredRouteShipments.filter(shipment => shipment.routeName === route.routeName ));
            }
            return route;
        });
        this.routeWithStops.replace(routeWithStops)
    }

    assignColorToRoute(routesList, routeMapColorList) {
        const colorCount = routeMapColorList.length
        return routesList.map((route, index)=>{
            route.color = routeMapColorList[index % colorCount ];
            route.routeKey = route.routeName;
            return route;
        });
    }

  isCustomerClosedForRoutes = ({routeId, stopsList, shipmentList}) => {
    const routeIds = routeId?.toLowerCase();
    const hasClosedCustomer = [...stopsList, ...shipmentList].some(
      (stop) => stop.routeId?.toLowerCase() === routeIds && stop.isCustomerClosed
    );
    return hasClosedCustomer;
  };

  @computed get routesPanelLabel(){
        return `${this.routesList.length} Routes`;
    }

    @computed get routeNameList(){
        return this.routesList.map(route => ({label: route.routeName, value: route.routeId, isTrapRoute: route.isTrapRoute}));
    }

    @computed get deliveryRouteNameList(){
        return this.routesList.filter(route => !route.isTrapRoute).map(route => ({label: route.routeName, value: route.routeId}));
    }

    // SHIPMENTS

    @action filterPlannedShipments(){
        this.plannedShipmentsList.replace(this.shipmentsList.filter(shipment => shipment.routeName));
    }

    @action filterRouteShipments(){
        if(this.selectedRouteNameList.length > 0){
            const filteredShipments = this.plannedShipmentsList.filter(shipment=>this.selectedRouteNameList.includes(shipment.routeName));
            this.filteredRouteShipments.replace(cloneDeep(filteredShipments));
        }
        else{
            this.filteredRouteShipments.replace(cloneDeep(this.plannedShipmentsList));
        }
    }

    @action filterShipments(){
        if(this.quickFilter){
            this.filteredShipments.replace(getFilteredData(this.shipmentsList, this.quickFilter, this.planDate));
        } else {
            this.filteredShipments.replace(this.shipmentsList);
        }

    }

    @action updateFilteredShipments(filteredData){
        if(filteredData){
            this.filteredShipmentsOnMap.replace(filteredData);
        }else{
            this.filteredShipmentsOnMap.replace(this.shipmentsList);
        }
    }

    @action updateQuickFilter(updatedQuickFilterString){
        // clear shipments only when quick filter has changed
        if(this.quickFilter !== updatedQuickFilterString){
            this.updateSelectedShipments([]);
        }
        this.quickFilter = updatedQuickFilterString;
        this.filterShipments();
    }

    @action setIsLassoRouteCreated(isLassoRouteCreated){
        this.isLassoRouteCreated = isLassoRouteCreated;
    }

    @action setIsPublishInProgress(isInProgress){
        this.isPublishInProgress = isInProgress;
    }

    @action setIsPrintManifestInProgress(isInProgress){
        this.isPrintManifestInProgress = isInProgress;
    }

    @action getRouteStops(routeId){
        return this.stopsList.filter(
          (stop) => stop.routeId?.toLowerCase() === routeId?.toLowerCase()
        );
    }

    @action getRoute(routeId){
        return this.routesList.find(
          (route) => route.routeId?.toLowerCase() === routeId?.toLowerCase()
        ) ?? null;
    }
    @action setStaticClientData(clientData) {
        this.staticClientData = clientData;
    }
      
    @action getStaticClientData() {
      return this.staticClientData;    
    }
    
    @action setDoorList(doorList){
        //sort in ascending order
        this.doorList = doorList.sort((a, b) => Number(a) - Number(b));
    }

    @action updateQuickFilterList(quickFilterList){
        this.quickFilterList =quickFilterList;
    }

    @computed get doorDataList() {
        return this.doorList.map((door) => ({ label: door, value: door })) || [];
    }
    @computed get serviceLevelList() {
        return this.staticClientData?.serviceLevelList || [];
    }
    @action setDriverPreferencesList(driverData) {
        return this.driverPreferencesList.replace(
            driverData.map((driver) => ({
            label: driver.driverName,
            value: driver.driverId,
            labelSuffix: `(${driver.driverId})`
            }))
        );
    }
    @action setHolidayOptions(options){
        this.holidayOptions.replace(options);
    }
    @action showErrorMessageOnGrid(gridName, value) {
        switch (gridName) {
          case 'shipmentGridMessage':
            this.shipmentGridMessage = value;
            break;
          case 'stopGridMessage':
            this.stopGridMessage = value;
            break;
          case 'routeGridMessage':
            this.routeGridMessage = value;
            break;
          case 'all':
            this.shipmentGridMessage = value;
            this.stopGridMessage = value;
            this.routeGridMessage = value;
            break;
        }
      }
    @action updateRouteMapControls(settingName) {
        this.routeMapControls[settingName] = !this.routeMapControls[settingName];
    }
    @action updateShipmentAutoSelectedFilter(autoSelectedFilter){
        this.shipmentAutoSelectedFilter.replace(autoSelectedFilter || []);
    }

    @computed get routeWithTrailerName(){
        return this.routesList.reduce((acc, route) => {
            if(route.routeId){
                acc[route.routeId] = route.trailerName;
            }
            return acc;
        }, {});
    }

    @action.bound getTrailerNameForRoute(routeId){
        if(!routeId) return null;
        return this.routeWithTrailerName[routeId] || null;
    }
}
export default new IBPlanningStore;